import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal } from "../components/motion/Reveal";
import { QuestionList } from "../components/shared/QuestionList";
import { FAQS, FAQ_CATEGORIES } from "../data/faq";

export default function FAQ() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter(
      (f) =>
        (category === "all" || f.category === category) &&
        (!q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
    );
  }, [query, category]);

  return (
    <div data-testid="faq-page">
      <PageHeader
        testId="faq-header"
        eyebrow="Frequently Asked Questions"
        lines={["Honest questions,", "sourced answers."]}
        lede="The things people actually ask when they first encounter the Noahide path — about belonging, prayer, Sabbath, conversion and where to begin. Each answer points back to the sources."
      />

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <label className="relative flex items-center border-b hairline">
                <Search size={16} className="absolute left-0 text-ink-muted" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search questions…"
                  data-testid="faq-search-input"
                  className="w-full bg-transparent py-3 pl-7 pr-2 font-serif text-xl text-ink placeholder:text-ink-muted/60 focus:outline-none"
                />
              </label>
              <p className="eyebrow mt-10">Themes</p>
              <ul className="mt-4 flex flex-wrap gap-2 lg:flex-col lg:gap-0" role="group" aria-label="Filter questions">
                {FAQ_CATEGORIES.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setCategory(c.id)}
                      data-testid={`faq-filter-${c.id}`}
                      aria-pressed={category === c.id}
                      className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors duration-300 lg:w-full lg:rounded-none lg:border-0 lg:border-l lg:px-4 lg:py-2.5 lg:text-left lg:text-sm ${
                        category === c.id
                          ? "border-ink bg-ink text-parchment lg:border-terracotta lg:bg-transparent lg:text-terracotta"
                          : "hairline text-ink-soft hover:text-ink lg:border-ink/10"
                      }`}
                    >
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
              <p data-testid="faq-result-count" className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-muted">
                {filtered.length} of {FAQS.length} questions
              </p>
            </div>
          </div>

          <div className="lg:col-span-8 lg:col-start-5">
            <Reveal>
              {filtered.length > 0 ? (
                <QuestionList items={filtered} idPrefix="faq" />
              ) : (
                <p data-testid="faq-empty-state" className="font-serif text-2xl text-ink-soft">
                  No questions match that search. Try “convert”, “pray” or “kosher”.
                </p>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
